// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';

import Navbar from './components/Navbar';
import LandingPage from './views/LandingPage';
import UnauthorizedPage from './views/UnauthorizedPage';
import GetStarted from './views/GetStarted';
import AffiliateSignup from './views/AffiliateSignup';
import Team from './views/Team';             // ← Team page

import './App.css';

export default function App() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
  } = useAuth0();

  const location = useLocation();
  const [permissions, setPermissions] = React.useState([]);
  const [permsLoading, setPermsLoading] = React.useState(false);

  // ── fetch custom permissions once logged in ────────────────────────────
  React.useEffect(() => {
    const loadPerms = async () => {
      if (!isAuthenticated) {
        setPermissions([]);
        return;
      }
      setPermsLoading(true);
      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'read:musukoteam read:musukoclient read:musukosupport',
        });
        const { permissions: list = [] } = jwtDecode(token);
        setPermissions(list);
      } catch (err) {
        console.error('Failed to load permissions', err);
        setPermissions([]);
      } finally {
        setPermsLoading(false);
      }
    };
    loadPerms();
  }, [isAuthenticated, getAccessTokenSilently]);

  // ── loading gate ───────────────────────────────────────────────────────
  if (authLoading || (isAuthenticated && permsLoading)) {
    return (
      <div className="app-shell">
        <main className="app-main-content">Loading…</main>
      </div>
    );
  }

  // ── permission helpers ────────────────────────────────────────────────
  const hasTeam = permissions.includes('read:musukoteam');

  // ── determine if we should show the original navbar ────────────────────
  const shouldShowOriginalNavbar = (!isAuthenticated && !location.pathname.startsWith('/affiliate')) || 
    location.pathname.startsWith('/getstarted');

  // ── top-level routing ─────────────────────────────────────────────────
  return (
    <>
      {/* Show navbar for non-authenticated users OR when on Get Started page */}
      {shouldShowOriginalNavbar && <Navbar />}

      <div className="app-shell">
        <main className="app-main-content">
          <Routes>
            {/* Public onboarding */}
            <Route path="/getstarted/*" element={<GetStarted />} />
            
            {/* Public affiliate signup */}
            <Route path="/affiliate" element={<AffiliateSignup />} />

            {/* Team area (guarded) */}
            {hasTeam && (
              <Route
                path="/team/*"
                element={
                  <Team
                    permissions={permissions}
                  />
                }
              />
            )}

            {/* Root and catch-all */}
            <Route
              path="/*"
              element={
                !isAuthenticated ? (
                  <LandingPage />
                ) : hasTeam ? (
                  /* Logged-in Team users land here */
                  <Navigate to="/team" replace />
                ) : (
                  <UnauthorizedPage />
                )
              }
            />
          </Routes>
        </main>

        <footer className="app-footer">{/* optional footer */}</footer>
      </div>
    </>
  );
}
